/* eslint-disable class-methods-use-this */

import {last} from 'lodash';

import {AtomParamTypesEnum, UninferredAtom} from '../expressionAtomModel';
import {buildEnumParameter, buildParameter, buildStringTemplateParameter} from '../expressionParamHelpers';
import {
  BooleanResult,
  buildListResultOf,
  InferredType,
  isListType,
  Method,
  MethodCategoriesEnum,
  NumberResult,
  ResultType,
  ResultTypesEnum,
  StaticallyTypedMethod,
  StringMethod,
  UnknownResult,
} from './internal';

export class ListMethod extends StaticallyTypedMethod {
  constructor(category: MethodCategoriesEnum, inferredItemType: InferredType) {
    super(category, buildListResultOf(inferredItemType));
  }
}

class ListItemGetter extends Method {
  constructor() {
    super(MethodCategoriesEnum.ITEM);
  }

  inferType(atom: UninferredAtom): InferredType {
    return isListType(atom.previousInfer) ? atom.previousInfer.itemType : UnknownResult;
  }
}

export class FunctionStart extends Method {
  constructor() {
    super(MethodCategoriesEnum.PROPERTY);
  }

  inferType(atom: UninferredAtom): InferredType {
    return atom.previousInfer;
  }
}

class NthListItemGetter extends ListItemGetter {
  getParameters() {
    return [buildParameter('n', AtomParamTypesEnum.NUMBER)];
  }
}

class SliceList extends Method {
  constructor() {
    super(MethodCategoriesEnum.ITEM);
  }

  inferType(atom: UninferredAtom) {
    return atom.previousInfer;
  }

  getParameters() {
    return [buildParameter('n', AtomParamTypesEnum.NUMBER)];
  }

  getAtomDefaultParameters() {
    return {n: 3};
  }
}

abstract class ListIterator extends Method {
  constructor(category: MethodCategoriesEnum) {
    super(category, {hasFunction: true});
  }

  inferFunctionStartType(previousInfer: InferredType) {
    return isListType(previousInfer) ? previousInfer.itemType : UnknownResult;
  }
}

class FilterMethod extends ListIterator {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM);
  }

  inferType(atom: UninferredAtom) {
    return atom.previousInfer;
  }
}

class MapMethod extends ListIterator {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM);
  }

  inferType(atom: UninferredAtom): InferredType {
    if (!atom.functionAtoms) {
      return UnknownResult;
    }

    // If map is empty, assume an identity transform.
    const resultAtom = last(atom.functionAtoms);
    if (!resultAtom) {
      return atom.previousInfer;
    }

    // If the sub-expression builds a list, it will be flattened.
    if (isListType(resultAtom.inferredType)) {
      return resultAtom.inferredType;
    }

    return buildListResultOf(resultAtom.inferredType);
  }
}

class FindMethod extends ListIterator {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM);
  }

  inferType(atom: UninferredAtom): InferredType {
    return isListType(atom.previousInfer) ? atom.previousInfer.itemType : UnknownResult;
  }
}

enum SortDirectionsEnum {
  ASCENDING = 'ascending',
  DESCENDING = 'descending',
}

class SortByMethod extends ListIterator {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM);
  }

  getParameters() {
    return [buildEnumParameter('direction', SortDirectionsEnum, SortDirectionsEnum.ASCENDING)];
  }

  inferType(atom: UninferredAtom) {
    return atom.previousInfer;
  }
}

class Deduplicate extends Method {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM);
  }

  inferType(atom: UninferredAtom) {
    return atom.previousInfer;
  }
}

export enum JoinSeparatorsEnum {
  COMMA = 'comma',
  EMPTY = 'empty',
  LINE = 'line',
  SPACE = 'space',
  CUSTOM_JOIN = 'custom_join',
}

class Join extends StringMethod {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM);
  }

  getParameters() {
    return [buildEnumParameter('separator', JoinSeparatorsEnum), buildStringTemplateParameter('custom_join')];
  }
}

export const listType: ResultType = {
  name: ResultTypesEnum.LIST,
  properties: {
    length: NumberResult,
    is_empty: BooleanResult,
    is_not_empty: BooleanResult,
  },
  methods: {
    first: new ListItemGetter(),
    last: new ListItemGetter(),
    nth: new NthListItemGetter(),
    beginning: new SliceList(),
    end: new SliceList(),
    filter: new FilterMethod(),
    map: new MapMethod(),
    find: new FindMethod(),
    sort_by: new SortByMethod(),
    dedup: new Deduplicate(),
    join: new Join(),
  },
};
